import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { UserInfoCard } from '@simOn/common/user-info-card-models';
import { AddTicketCommentInterface, UpdateTicketCommentInterface } from '@simOn/ticket/comments/models';
import {
  ICreateTicket,
  ITicketComment,
  ITicketStatusUpdate,
  IUpdateTicket,
  Ticket,
  TicketListFiltersWithPage,
  TicketPaginated
} from '@simOn/ticket/element/models';
import { isNewTicket } from '@simOn/ticket/models';
import { BuildHttpParamsFromFilters } from '@simOn/utils/helpers';
import { API_URL } from '@simOn/utils/tokens';
import { Observable, Subject, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TicketsApiService {
  private readonly _onAdd = new Subject<string>();
  private readonly _onUpdate = new Subject<string>();
  private readonly _onDelete = new Subject<{ id: string; name: string }>();
  private readonly _onMarkAsRead = new Subject<string>();
  private readonly _onUpdateStatus = new Subject<ITicketStatusUpdate>();
  private readonly http = inject(HttpClient);
  private readonly _apiUrl = inject(API_URL);

  readonly onAdd$ = this._onAdd.pipe(switchMap((id) => this.getTicket$(id)));
  readonly onUpdate$ = this._onUpdate.pipe(switchMap((id) => this.getTicket$(id)));
  readonly onDelete$ = this._onDelete.asObservable();
  readonly onMarkAsRead$ = this._onMarkAsRead.asObservable();
  readonly onUpdateStatus$ = this._onUpdateStatus.asObservable();

  getTicket$(ticketId: string): Observable<Ticket> {
    return this.http.get<Ticket>(`${this._apiUrl}/Tickets/GetTicket?TicketId=${ticketId}`);
  }

  getTickets$(): Observable<Partial<Ticket[]>> {
    return this.http.get<Partial<Ticket[]>>(`${this._apiUrl}/Tickets/GetTickets`);
  }

  getTicketsPaginated$(filters: Partial<TicketListFiltersWithPage>) {
    let params = BuildHttpParamsFromFilters<TicketListFiltersWithPage>(filters);
    return this.http.get<TicketPaginated>(`${this._apiUrl}/Tickets/GetTicketsPaginated`, { params });
  }

  getTicketIndex(id: string, filters: Partial<TicketListFiltersWithPage>): Observable<number> {
    let params = BuildHttpParamsFromFilters<TicketListFiltersWithPage>(filters).set('ticketId', id);
    return this.http.get<number>(`${this._apiUrl}/Tickets/GetTicketIndex`, { params });
  }

  getUsersForTicket$(ticketId: string): Observable<UserInfoCard[]> {
    return this.http.get<UserInfoCard[]>(
      `${this._apiUrl}/Tickets/GetUsersAssignableToTicket${!isNewTicket(ticketId) ? `?TicketId=${ticketId}` : ''}`
    );
  }
  createTicket$(payload: ICreateTicket): Observable<string> {
    return this.http
      .post<string>(`${this._apiUrl}/Tickets/CreateTicket`, payload)
      .pipe(tap((id) => this._onAdd.next(id)));
  }

  addTicketComment$(payload: AddTicketCommentInterface): Observable<ITicketComment> {
    return this.http.post<ITicketComment>(`${this._apiUrl}/Tickets/AddTicketComment`, payload);
  }

  updateTicket$(payload: IUpdateTicket): Observable<void> {
    return this.http
      .put<void>(`${this._apiUrl}/Tickets/UpdateTicket`, payload)
      .pipe(tap(() => this._onUpdate.next(payload.ticketId)));
  }

  markTicketAsRead$(ticketId: string): Observable<void> {
    return this.http
      .put<void>(`${this._apiUrl}/Tickets/MarkTicketAsRead?TicketId=${ticketId}`, {})
      .pipe(tap(() => this._onMarkAsRead.next(ticketId)));
  }

  updateTicketComment$(payload: UpdateTicketCommentInterface): Observable<void> {
    return this.http.put<void>(`${this._apiUrl}/Tickets/UpdateTicketComment`, payload);
  }

  updateTicketStatus$(payload: ITicketStatusUpdate): Observable<void> {
    return this.http
      .put<void>(`${this._apiUrl}/Tickets/UpdateTicketStatus`, payload)
      .pipe(tap(() => this._onUpdateStatus.next(payload)));
  }

  deleteTicket$(id: string, name: string): Observable<void> {
    return this.http
      .delete<void>(`${this._apiUrl}/Tickets/DeleteTicket?TicketId=${id}`)
      .pipe(tap(() => this._onDelete.next({ id, name })));
  }

  deleteAttachmentsFromTicket$(ticketId: string, attachmentsIds: string[]): Observable<string> {
    return this.http.delete<string>(
      `${this._apiUrl}/Tickets/DeleteAttachmentsFromTicket?TicketId=${ticketId}&AttachmentIds=${attachmentsIds}`
    );
  }

  deleteTicketComment$(ticketCommentId: string): Observable<void> {
    return this.http.delete<void>(`${this._apiUrl}/Tickets/DeleteTicketComment?TicketCommentId=${ticketCommentId}`);
  }

  getRoomTicketsCount$(roomId: string): Observable<number> {
    return this.http.get<number>(`${this._apiUrl}/Tickets/GetRoomTicketsCount`, {
      params: { RoomId: roomId }
    });
  }
}
